<template>
  <div v-if="test" class="p-6">
    <!-- Title and Edit Section -->
    <div class="flex justify-center items-center mb-6">
      <div v-if="!isEditingName" class="text-center">
        <h1 class="text-3xl font-bold text-gray-800">
          {{ test.name }}
          <button @click="enableNameEditing" class="ml-2 text-gray-500 hover:text-gray-700">
            <svg class="h-6 w-6 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-1.5a2.5 2.5 0 113.536 3.536L7.5 21H3v-4.5L15.232 5.232z" />
            </svg>
          </button>
        </h1>
      </div>
      <div v-else class="text-center">
        <input
            v-model="newTestName"
            @keyup.enter="updateTestName"
            @blur="updateTestName"
            class="text-3xl font-bold text-center text-gray-800 border-b-2 border-gray-300 focus:outline-none focus:border-blue-500"
            ref="nameInput"
        />
      </div>
    </div>

    <!-- Main Actions Section -->
    <div class="flex justify-between items-start mb-8">
      <div v-if="qrCode" class="flex items-center space-x-4">
        <img :src="qrCode" alt="QR Code for scanning student tests" class="w-32 h-32" />
        <div>
          <h2 class="font-semibold text-gray-700">Scan Student Answers</h2>
          <p class="text-gray-600 text-sm mt-1">
            Use your phone to scan this QR code. It will open a camera page to upload student answer sheets.
          </p>
        </div>
      </div>
      <div class="flex flex-col space-y-2 items-end">
        <!-- ICONS ADDED TO BUTTONS -->
        <button @click="goToQuestionsWizard" class="bg-blue-500 text-white px-4 py-2 rounded w-full hover:bg-blue-600 transition flex items-center justify-end">
          <span class="mr-2">Setup Questions & Answers</span>
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
        </button>
        <button @click="checkAllStudents" class="bg-yellow-500 text-white px-4 py-2 rounded w-full hover:bg-yellow-600 transition flex items-center justify-end">
          <span class="mr-2">Check All Students</span>
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </button>
        <button @click="viewAllResults" class="bg-green-500 text-white px-4 py-2 rounded w-full hover:bg-green-600 transition flex items-center justify-end">
          <span class="mr-2">View Results</span>
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75c0 .621-.504 1.125-1.125 1.125h-2.25A1.125 1.125 0 013 21v-7.875zM12.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v12c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125v-12zM8.25 15c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v6c0 .621-.504 1.125-1.125 1.125h-2.25A1.125 1.125 0 018.25 21v-6z" /></svg>
        </button>
      </div>
    </div>

    <!-- Students Table -->
    <div v-if="students.length" class="overflow-x-auto">
      <h2 class="text-xl font-semibold mb-4 text-gray-700">Students</h2>
      <table class="min-w-full bg-white border border-gray-300 rounded-lg shadow">
        <thead>
        <!-- Table Header is unchanged -->
        <tr>
          <th class="py-3 px-6 text-left text-sm font-medium text-gray-700 bg-gray-100 border-b">Student Name</th>
          <th class="py-3 px-6 text-left text-sm font-medium text-gray-700 bg-gray-100 border-b">Correct</th>
          <th class="py-3 px-6 text-left text-sm font-medium text-gray-700 bg-gray-100 border-b">Incorrect</th>
          <th class="py-3 px-6 text-left text-sm font-medium text-gray-700 bg-gray-100 border-b">Points</th>
          <th class="py-3 px-6 text-left text-sm font-medium text-gray-700 bg-gray-100 border-b">Mark</th>
          <th class="py-3 px-6 text-left text-sm font-medium text-gray-700 bg-gray-100 border-b">Actions</th>
          <th class="py-3 px-6 text-center text-sm font-medium text-gray-700 bg-gray-100 border-b">Status</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="student in students" :key="student.id" class="border-b hover:bg-gray-50">
          <td class="py-3 px-6 text-sm text-gray-700">{{ student.name }}</td>
          <td class="py-3 px-6 text-sm text-gray-700">{{ student.correctAnswers }}</td>
          <td class="py-3 px-6 text-sm text-gray-700">{{ student.incorrectAnswers }}</td>
          <td class="py-3 px-6 text-sm text-gray-700">{{ student.points }}</td>
          <td class="py-3 px-6 text-sm text-gray-700">{{ student.mark }}</td>

          <!-- #### ACTION BUTTONS UPDATED #### -->
          <td class="py-3 px-6 text-sm">
            <div class="flex items-center space-x-2">
              <button @click="goToStudentWork(student.id)" class="text-white bg-purple-500 hover:bg-purple-600 px-3 py-1 rounded-md text-xs transition">
                Manage Work
              </button>
              <!-- "Result" is now a styled button -->
              <button @click="goToStudentResult(student.id)" class="text-white bg-blue-500 hover:bg-blue-600 px-3 py-1 rounded-md text-xs transition">
                Result
              </button>
              <!-- "Delete" is now an icon button with a tooltip -->
              <button @click="deleteStudent(student.id)" class="text-gray-500 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition" title="Delete Student">
                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
            </div>
          </td>
          <!-- #### END OF UPDATES #### -->

          <td class="py-3 px-6 text-sm text-center">
            <div v-if="student.status === 'CHECKING'" class="flex justify-center">
              <svg class="animate-spin h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke-width="4" stroke="currentColor"></circle>
                <path class="opacity-75" d="M4 12a8 8 0 018-8V4a10 10 0 00-10 10h2zm0 0a8 8 0 018 8v-2a6 6 0 00-6-6H4z" fill="currentColor"></path>
              </svg>
            </div>
            <div v-else-if="student.status === 'CHECKED'">✔️</div>
            <div v-else-if="student.status === 'TODO' || student.status === 'PENDING'">❓</div>
            <div v-else-if="student.status === 'FAILED'">❌</div>
            <div v-else>❓</div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Add Student Button -->
    <div class="flex justify-end mt-4">
      <!-- ICON ADDED TO BUTTON -->
      <button @click="createStudent" class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition flex items-center">
        <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        <span>Add Student</span>
      </button>
    </div>
  </div>
  <div v-else class="text-center p-10">
    <p>Loading test details...</p>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { onBeforeRouteUpdate, useRouter } from 'vue-router'
import SockJS from 'sockjs-client'
import Stomp from 'stompjs'
import axios from '../services/axiosInstance'
import { useModalStore } from '@/stores/modal' // IMPORT

const props = defineProps({ testId: { type: String, required: true } })
const router = useRouter()
const modal = useModalStore()

const test = ref(null)
const students = ref([])
const qrCode = ref(null)
const isEditingName = ref(false)
const newTestName = ref('')
const nameInput = ref(null)
const stompClient = ref(null)

const fetchData = async () => {
  if (!props.testId) {
    console.error('Test ID is missing, cannot fetch data.')
    return
  }

  try {
    const [testResponse, studentsResponse, qrCodeResponse] = await Promise.all([
      axios.get(`/tests/${props.testId}`),
      axios.get(`/tests/${props.testId}/students`),
      axios.get(`/generate-qr-code/${props.testId}`, { responseType: 'arraybuffer' })
    ])

    test.value = testResponse.data
    newTestName.value = test.value.name
    students.value = studentsResponse.data.map(s => ({ ...s, status: s.status || 'TODO' }))

    const base64QRCode = btoa(new Uint8Array(qrCodeResponse.data).reduce((data, byte) => data + String.fromCharCode(byte), ''))
    qrCode.value = `data:image/png;base64,${base64QRCode}`

    setupWebSocket()
  } catch (error) {
    console.error('Error fetching initial data:', error)
  }
}

const setupWebSocket = () => {
  const socketUrl = process.env.VUE_APP_WS_URL
  const socket = new SockJS(socketUrl)
  stompClient.value = Stomp.over(socket)
  stompClient.value.debug = null

  stompClient.value.connect({}, () => {
    if (!props.testId) return
    stompClient.value.subscribe(`/topic/tests/${props.testId}/status`, message => {
      const body = JSON.parse(message.body)

      if (!body.student || !body.student.id) {
        console.warn('Received WebSocket message without a valid student object:', body)
        return
      }

      console.log('Received WebSocket message:', body)

      const studentId = body.student.id
      const studentIndex = students.value.findIndex(s => s.id === studentId)

      // --- THE FIX IS HERE ---
      if (studentIndex !== -1) {
        // --- This is the UPDATE logic (for existing students) ---
        const existingStudent = students.value[studentIndex]
        const updatedStudent = { ...existingStudent, ...body.student }
        students.value.splice(studentIndex, 1, updatedStudent)
      } else {
        // --- This is the ADD logic (for new students) ---
        console.log('Adding new student to the list:', body.student)
        students.value.push(body.student)
      }
      // --- END OF FIX ---
    })
  }, error => {
    console.error('WebSocket connection error:', error)
  })
}

const enableNameEditing = async () => {
  isEditingName.value = true
  await nextTick()
  nameInput.value?.focus()
}

const updateTestName = async () => {
  if (!newTestName.value || newTestName.value === test.value.name) {
    isEditingName.value = false
    return
  }
  try {
    await axios.put(`/tests/${props.testId}`, { name: newTestName.value })
    test.value.name = newTestName.value
  } catch (error) {
    console.error('Error updating test name:', error)
  } finally {
    isEditingName.value = false
  }
}

const deleteStudent = async (studentId) => {
  // REPLACE native confirm
  const confirmed = await modal.confirm(
    'Are you sure you want to delete this student?',
    'Delete Student',
    'Delete'
  )
  if (!confirmed) return

  try {
    await axios.delete(`/tests/${props.testId}/students/${studentId}`)
    students.value = students.value.filter(s => s.id !== studentId)
    modal.success('Student deleted successfully.')
  } catch (error) {
    console.error('Error deleting student:', error)
    modal.error('Failed to delete student.')
  }
}

const checkAllStudents = async () => {
  const uncheckedStudents = students.value.filter(s => s.status !== 'CHECKED' && s.pages?.length > 0)
  if (uncheckedStudents.length === 0) {
    modal.alert('All students with uploaded pages have already been checked.') // REPLACE alert
    return
  }
  try {
    await axios.post(`/tests/${props.testId}/checkall`)
    modal.success('Batch checking started.')
  } catch (error) {
    console.error('Error checking all students:', error)
    modal.error('Error initiating batch check.')
  }
}

const createStudent = async () => {
  try {
    // Fire and forget; the WebSocket will now correctly handle the UI update.
    await axios.post(`/tests/${props.testId}/students`)
  } catch (error) {
    console.error('Error creating student:', error)
  }
}

const goToQuestionsWizard = () => {
  router.push({ name: 'QuestionsUpload', params: { testId: props.testId } })
}

const viewAllResults = () => {
  router.push(`/tests/${props.testId}/results`)
}

const goToStudentWork = (studentId) => {
  router.push({
    name: 'StudentWorkUpload',
    params: { testId: props.testId, studentId }
  })
}

const goToStudentResult = (studentId) => {
  router.push({
    name: 'DetailedReport',
    params: {
      testId: props.testId,
      studentId
    }
  })
}

onMounted(fetchData)

onBeforeRouteUpdate(async (to, from) => {
  if (to.params.testId === from.params.testId) {
    console.log('Re-fetching data for TestDetail page...')
    await fetchData()
  }
})
</script>
